import { Controller } from "@hotwired/stimulus"
import { loadPdfjs } from "../lib/pdfjs_loader"
import { pdfDocumentSource } from "../lib/pdf_document_source"

const PORTRAIT_QUERY = "(max-width: 900px)"
const PRELOAD_RADIUS = 2
// How far either side of the current page a painted canvas is kept. Deliberately wider than
// PRELOAD_RADIUS: a page that has just left the render window is still on screen during a
// flip animation, and flipping back a spread must not expose a blank while it repaints.
// Ten retained pages at roughly 6MB each puts the ceiling near 63MB, flat, whatever the
// document's length — each extra page of slack costs about another 6MB.
const RETAIN_RADIUS = 4
// Past 2x there is nothing further to see at this page width, and a canvas grows with the
// square of the ratio, so a 3x phone would otherwise pay ~14MB per page instead of ~6MB.
const MAX_OUTPUT_SCALE = 2
// Zoom raises CSS page size and therefore paint pixels. 4MP (~16MB RGBA) is enough for
// readable body type without letting a fullscreen 3x spread on a 2x display run away.
const MAX_CANVAS_PIXELS = 4_000_000
const DEFAULT_ASPECT = 11 / 8.5
const MAX_PAGE_HEIGHT = 900
const MIN_ZOOM = 1
const MAX_ZOOM = 3
const ZOOM_STEP = 1.25

export default class extends Controller {
  static targets = [
    "stage",
    "bookHost",
    "bookTemplate",
    "pageTemplate",
    "loading",
    "error",
    "toolbar",
    "prev",
    "next",
    "progress",
    "zoomOut",
    "zoomIn",
    "zoomLevel",
    "fullscreenButton",
    "fullscreenIcon"
  ]

  static values = { url: String, byteSize: Number }

  connect() {
    this.alive = true
    this.pdfDoc = null
    this.pageFlip = null
    this.canvases = []
    this.renderedPages = new Set()
    // Pages with a paint in flight. A canvas must not be released out from under an
    // unfinished render, so eviction skips these and collects them on the next flip.
    this.renderingPages = new Set()
    this.pageAspect = DEFAULT_ASPECT
    this.pageWidth = 0
    this.rebuildTimer = null
    this.zoom = MIN_ZOOM

    this.portraitQuery = window.matchMedia(PORTRAIT_QUERY)
    this.boundOrientation = this.scheduleRebuild.bind(this)
    this.boundFullscreen = this.onFullscreenChange.bind(this)
    this.portraitQuery.addEventListener("change", this.boundOrientation)
    document.addEventListener("fullscreenchange", this.boundFullscreen)
    document.addEventListener("webkitfullscreenchange", this.boundFullscreen)
    this.boundTurnTo = (event) => this.turnToPageNumber(event.detail?.page)
    this.element.addEventListener("pdf-flipbook:turn-to", this.boundTurnTo)

    this.updateZoomControls()
    this.updateFullscreenButton()
    this.loadBook()
  }

  disconnect() {
    this.alive = false
    this.portraitQuery?.removeEventListener("change", this.boundOrientation)
    document.removeEventListener("fullscreenchange", this.boundFullscreen)
    document.removeEventListener("webkitfullscreenchange", this.boundFullscreen)
    this.element.removeEventListener("pdf-flipbook:turn-to", this.boundTurnTo)
    clearTimeout(this.rebuildTimer)
    this.leaveExpanded()
    this.teardownBook()
    this.pdfDoc?.destroy()
    this.pdfDoc = null
  }

  async loadBook() {
    if (!this.urlValue) return

    this.showLoading()

    try {
      const pdfjsLib = await loadPdfjs()
      if (!this.alive) return

      // Opened by URL rather than read into an ArrayBuffer first, so the first page paints as
      // soon as its bytes have arrived instead of after the entire file has downloaded. Past a
      // size threshold the rest is never fetched at all until it is read — see
      // lib/pdf_document_source for why that needs the file served straight from storage.
      const pdfDoc = await pdfjsLib.getDocument(
        pdfDocumentSource(this.urlValue, this.byteSizeValue)
      ).promise
      if (!this.alive) {
        pdfDoc.destroy()
        return
      }

      this.pdfDoc = pdfDoc

      const firstPage = await this.pdfDoc.getPage(1)
      const viewport = firstPage.getViewport({ scale: 1 })
      this.pageAspect = viewport.height / viewport.width

      await this.mountBook()
    } catch (error) {
      console.error("Error loading PDF flipbook:", error)
      if (this.alive) this.showError()
    }
  }

  async mountBook(startPage = 0) {
    if (!this.pdfDoc || !this.alive) return

    const { PageFlip } = await import("page-flip/dist/js/page-flip.module.js")
    if (!this.alive) return

    this.teardownBook()

    const book = this.bookTemplateTarget.content.firstElementChild.cloneNode(true)
    this.bookHostTarget.appendChild(book)
    this.canvases = this.buildPages(book, this.pdfDoc.numPages)

    const size = this.pageSize()
    this.pageWidth = size.width
    const reducedMotion = this.prefersReducedMotion
    const zoomed = this.zoom > MIN_ZOOM
    const portrait = window.matchMedia(PORTRAIT_QUERY).matches
    const spreadWidth = portrait ? size.width : size.width * 2

    this.element.classList.toggle("is-zoomed", zoomed)
    this.sizeBookHost(zoomed, spreadWidth, size.height)

    this.pageFlip = new PageFlip(book, {
      width: size.width,
      height: size.height,
      size: zoomed ? "fixed" : "stretch",
      minWidth: zoomed ? size.width : Math.max(200, Math.floor(size.width * 0.5)),
      maxWidth: size.width,
      minHeight: zoomed ? size.height : Math.max(280, Math.floor(size.height * 0.5)),
      maxHeight: size.height,
      showCover: true,
      usePortrait: true,
      autoSize: !zoomed,
      drawShadow: !reducedMotion,
      flippingTime: reducedMotion ? 1 : 700,
      maxShadowOpacity: 0.4,
      mobileScrollSupport: true,
      swipeDistance: 30,
      useMouseEvents: true,
      disableFlipByClick: false
    })

    // page-flip's fixed mode sizes the parent to one page when usePortrait is on,
    // which would collapse a landscape spread and cancel the zoom overflow.
    if (zoomed) {
      book.style.width = `${spreadWidth}px`
      book.style.minWidth = `${spreadWidth}px`
      book.style.maxWidth = `${spreadWidth}px`
      book.style.height = `${size.height}px`
      book.style.minHeight = `${size.height}px`
    }

    this.pageFlip.loadFromHTML(book.querySelectorAll(".academy-pdf-flipbook-page"))
    this.pageFlip.on("flip", (event) => this.onFlip(event.data))
    this.pageFlip.on("changeOrientation", () => this.onFlip(this.pageFlip.getCurrentPageIndex()))

    try {
      const bounds = this.pageFlip.getBoundsRect()
      if (bounds?.pageWidth) this.pageWidth = bounds.pageWidth
    } catch {
      // stretch mode can report no rect until the first paint; pageSize() stands in
    }

    const safeStart = Math.max(0, Math.min(startPage, this.pdfDoc.numPages - 1))
    if (safeStart > 0) this.pageFlip.turnToPage(safeStart)

    await this.renderAround(safeStart)
    if (!this.alive) return

    this.onFlip(this.pageFlip.getCurrentPageIndex())
    this.hideLoading()
  }

  buildPages(book, numPages) {
    const canvases = []

    for (let pageNumber = 1; pageNumber <= numPages; pageNumber += 1) {
      const page = this.pageTemplateTarget.content.firstElementChild.cloneNode(true)
      if (pageNumber === 1) page.dataset.density = "hard"
      book.appendChild(page)
      canvases.push(page.querySelector("canvas"))
    }

    return canvases
  }

  pageSize() {
    const stageWidth = this.stageTarget.clientWidth || 690
    const stageHeight = this.stageTarget.clientHeight || Math.floor(window.innerHeight * 0.7)
    const portrait = window.matchMedia(PORTRAIT_QUERY).matches
    const fullscreen = this.inFullscreen()
    const maxHeight = fullscreen ? stageHeight : Math.min(window.innerHeight * 0.7, MAX_PAGE_HEIGHT)
    let width = portrait ? stageWidth : (fullscreen ? stageWidth / 2 : Math.min(stageWidth / 2, 550))
    let height = width * this.pageAspect

    if (height > maxHeight) {
      height = maxHeight
      width = height / this.pageAspect
    }

    // Fit first, then enlarge, so zoom > 1 can overflow the stage instead of
    // being immediately clamped back to the same readable-but-tiny box.
    const zoom = this.zoom || MIN_ZOOM
    width *= zoom
    height *= zoom

    return { width: Math.max(Math.floor(width), 200), height: Math.max(Math.floor(height), 280) }
  }

  async renderAround(pageIndex) {
    const current = pageIndex + 1
    const renders = []

    for (let pageNumber = current - PRELOAD_RADIUS; pageNumber <= current + PRELOAD_RADIUS + 1; pageNumber += 1) {
      if (pageNumber >= 1 && pageNumber <= this.pdfDoc.numPages) {
        renders.push(this.renderPage(pageNumber))
      }
    }

    await Promise.all(renders)
  }

  async renderPage(pageNumber) {
    if (this.renderedPages.has(pageNumber) || !this.pdfDoc) return

    const canvas = this.canvases[pageNumber - 1]
    if (!canvas) return

    this.renderedPages.add(pageNumber)
    // Held by reference rather than read back through `this`: a rebuild swaps the set, and a
    // render still finishing against the old book must not clear the guard for the same page
    // number in the new one.
    const renderingPages = this.renderingPages
    renderingPages.add(pageNumber)

    try {
      const page = await this.pdfDoc.getPage(pageNumber)
      const outputScale = Math.min(window.devicePixelRatio || 1, MAX_OUTPUT_SCALE)
      const viewport = page.getViewport({ scale: 1 })
      const scaled = page.getViewport({ scale: this.paintScale(viewport, outputScale) })

      canvas.width = Math.floor(scaled.width)
      canvas.height = Math.floor(scaled.height)

      await page.render({
        canvasContext: canvas.getContext("2d"),
        viewport: scaled
      }).promise
    } catch (error) {
      this.renderedPages.delete(pageNumber)
      console.error("Error rendering flipbook page:", error)
    } finally {
      renderingPages.delete(pageNumber)
    }
  }

  paintScale(viewport, outputScale) {
    let scale = (this.pageWidth * outputScale) / viewport.width
    const area = viewport.width * scale * viewport.height * scale
    if (area > MAX_CANVAS_PIXELS) {
      scale *= Math.sqrt(MAX_CANVAS_PIXELS / area)
    }

    return scale
  }

  // A canvas holds a full-resolution bitmap of its page — around 6MB at this size — and
  // nothing used to release one, so reading a 100-page issue retained ~600MB in the tab:
  // enough for Safari to start blanking canvases and for Chrome to lose the tab. Pages
  // outside the retention window give their bitmap back and repaint if the reader returns.
  releaseDistantPages(pageIndex) {
    const current = pageIndex + 1
    const first = current - RETAIN_RADIUS
    const last = current + RETAIN_RADIUS + 1

    Array.from(this.renderedPages).forEach((pageNumber) => {
      if (pageNumber >= first && pageNumber <= last) return
      if (this.renderingPages.has(pageNumber)) return

      this.releasePage(pageNumber)
    })
  }

  // Zeroing the dimensions is what frees the bitmap — clearRect only paints over it. The CSS
  // sizes the canvas, so the page keeps its box and simply shows blank until it repaints.
  releasePage(pageNumber) {
    this.renderedPages.delete(pageNumber)

    const canvas = this.canvases[pageNumber - 1]
    if (!canvas) return

    canvas.width = 0
    canvas.height = 0
  }

  onFlip(pageIndex) {
    this.updateProgress(pageIndex)
    this.updateNav(pageIndex)
    this.announcePage(pageIndex)
    // Release before rendering, so the pages leaving the window give their memory back
    // before the ones entering it claim theirs.
    this.releaseDistantPages(pageIndex)
    this.renderAround(pageIndex)
  }

  announcePage(pageIndex) {
    const page = pageIndex + 1
    const total = this.pdfDoc?.numPages || page
    const portrait = this.pageFlip?.getOrientation() === "portrait"
    const cover = page === 1 && !portrait
    const lastAlone = page === total && !portrait
    const rightPage = (portrait || cover || lastAlone) ? page : Math.min(page + 1, total)

    this.element.dataset.pdfFlipbookPage = String(rightPage)
    this.dispatch("page-changed", { detail: { page, rightPage } })
  }

  turnToPageNumber(pageNumber) {
    if (!this.pageFlip || !this.pdfDoc) return

    const index = Math.max(0, Math.min(Number(pageNumber) - 1, this.pdfDoc.numPages - 1))
    if (!Number.isInteger(index) || index === this.pageFlip.getCurrentPageIndex()) return

    if (this.prefersReducedMotion) {
      this.pageFlip.turnToPage(index)
      this.onFlip(index)
      return
    }

    this.pageFlip.flip(index)
  }

  updateProgress(pageIndex) {
    if (!this.hasProgressTarget || !this.pdfDoc) return

    const total = this.pdfDoc.numPages
    const page = pageIndex + 1
    const portrait = this.pageFlip?.getOrientation() === "portrait"
    const cover = page === 1 && !portrait
    const lastAlone = page === total && !portrait

    if (portrait || cover || lastAlone) {
      this.progressTarget.textContent = `${page} of ${total}`
      return
    }

    const right = Math.min(page + 1, total)
    this.progressTarget.textContent = right > page ? `${page}–${right} of ${total}` : `${page} of ${total}`
  }

  updateNav(pageIndex) {
    if (!this.pdfDoc) return

    if (this.hasPrevTarget) this.prevTarget.disabled = pageIndex <= 0
    if (this.hasNextTarget) this.nextTarget.disabled = pageIndex >= this.pdfDoc.numPages - 1
  }

  prevPage() {
    if (!this.pageFlip || this.pageFlip.getCurrentPageIndex() <= 0) return

    if (this.prefersReducedMotion) {
      this.pageFlip.turnToPrevPage()
      this.onFlip(this.pageFlip.getCurrentPageIndex())
      return
    }

    this.pageFlip.flipPrev()
  }

  nextPage() {
    if (!this.pageFlip || this.pageFlip.getCurrentPageIndex() >= this.pdfDoc.numPages - 1) return

    if (this.prefersReducedMotion) {
      this.pageFlip.turnToNextPage()
      this.onFlip(this.pageFlip.getCurrentPageIndex())
      return
    }

    this.pageFlip.flipNext()
  }

  zoomIn() {
    this.setZoom(this.zoom * ZOOM_STEP)
  }

  zoomOut() {
    this.setZoom(this.zoom / ZOOM_STEP)
  }

  setZoom(zoom) {
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom))
    if (Math.abs(next - this.zoom) < 0.001) {
      this.updateZoomControls()
      return
    }

    this.zoom = next
    this.updateZoomControls()
    this.scheduleRebuild()
  }

  updateZoomControls() {
    if (this.hasZoomOutTarget) this.zoomOutTarget.disabled = this.zoom <= MIN_ZOOM + 0.001
    if (this.hasZoomInTarget) this.zoomInTarget.disabled = this.zoom >= MAX_ZOOM - 0.001
    if (this.hasZoomLevelTarget) this.zoomLevelTarget.textContent = `${Math.round(this.zoom * 100)}%`
  }

  toggleFullscreen() {
    if (this.inFullscreen()) {
      this.exitFullscreen()
      return
    }

    this.enterFullscreen()
  }

  enterFullscreen() {
    const request = this.element.requestFullscreen?.bind(this.element)
      || this.element.webkitRequestFullscreen?.bind(this.element)

    if (!request) {
      this.enterImmersive()
      return
    }

    Promise.resolve(request()).then(() => {
      if (this.alive) this.element.focus()
    }).catch(() => {
      if (this.alive) this.enterImmersive()
    })
  }

  exitFullscreen() {
    if (this.element.classList.contains("is-immersive")) {
      this.exitImmersive()
      return
    }

    const exit = document.exitFullscreen?.bind(document)
      || document.webkitExitFullscreen?.bind(document)

    if (exit) Promise.resolve(exit()).catch(() => {})
  }

  enterImmersive() {
    this.element.classList.add("is-immersive")
    this.updateFullscreenButton()
    this.element.focus()
    this.scheduleRebuild()
  }

  exitImmersive() {
    if (!this.element.classList.contains("is-immersive")) return

    this.element.classList.remove("is-immersive")
    this.updateFullscreenButton()
    this.scheduleRebuild()
  }

  onFullscreenChange() {
    if (!this.alive) return

    this.updateFullscreenButton()
    this.scheduleRebuild()
    if (this.inFullscreen()) this.element.focus()
  }

  inFullscreen() {
    return this.fullscreenElement() === this.element || this.element.classList.contains("is-immersive")
  }

  fullscreenElement() {
    return document.fullscreenElement || document.webkitFullscreenElement || null
  }

  updateFullscreenButton() {
    if (!this.hasFullscreenButtonTarget) return

    const active = this.inFullscreen()
    this.fullscreenButtonTarget.setAttribute("aria-pressed", String(active))
    this.fullscreenButtonTarget.setAttribute("aria-label", active ? "Exit fullscreen" : "Enter fullscreen")

    if (this.hasFullscreenIconTarget) {
      this.fullscreenIconTarget.classList.remove("ph-corners-out", "ph-corners-in")
      this.fullscreenIconTarget.classList.add(active ? "ph-corners-in" : "ph-corners-out")
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.element.classList.contains("is-immersive")) {
      event.preventDefault()
      this.exitImmersive()
      return
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.prevPage()
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      this.nextPage()
    } else if (event.key === "+" || event.key === "=") {
      event.preventDefault()
      this.zoomIn()
    } else if (event.key === "-") {
      event.preventDefault()
      this.zoomOut()
    }
  }

  scheduleRebuild() {
    if (!this.pdfDoc || !this.pageFlip) return

    clearTimeout(this.rebuildTimer)
    this.rebuildTimer = setTimeout(() => {
      const startPage = this.pageFlip?.getCurrentPageIndex() || 0
      this.renderedPages = new Set()
      this.mountBook(startPage)
    }, 200)
  }

  sizeBookHost(zoomed, spreadWidth, height) {
    if (!zoomed) {
      this.resetBookHostSize()
      return
    }

    this.bookHostTarget.style.width = `${spreadWidth}px`
    this.bookHostTarget.style.height = `${height}px`
    this.bookHostTarget.style.minHeight = `${height}px`
  }

  resetBookHostSize() {
    this.bookHostTarget.style.width = ""
    this.bookHostTarget.style.height = ""
    this.bookHostTarget.style.minHeight = ""
  }

  teardownBook() {
    if (this.pageFlip) {
      this.pageFlip.off("flip")
      this.pageFlip.off("changeOrientation")
      try {
        this.pageFlip.destroy()
      } catch {
        // destroy() removes the book node; a second call or a failed init is a no-op
      }
      this.pageFlip = null
    }

    // A rebuild (orientation or resize) builds a second full set of canvases while these are
    // still referenced by the detached book, so hand their bitmaps back here rather than
    // waiting on the garbage collector to notice mid-resize.
    this.canvases.forEach((canvas) => {
      if (!canvas) return

      canvas.width = 0
      canvas.height = 0
    })

    this.resetBookHostSize()
    this.bookHostTarget.replaceChildren()
    this.canvases = []
    this.renderingPages = new Set()
  }

  leaveExpanded() {
    if (this.fullscreenElement() === this.element) {
      const exit = document.exitFullscreen?.bind(document)
        || document.webkitExitFullscreen?.bind(document)

      if (exit) Promise.resolve(exit()).catch(() => {})
    }

    this.element.classList.remove("is-immersive")
  }

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = false
    if (this.hasErrorTarget) this.errorTarget.hidden = true
    if (this.hasToolbarTarget) this.toolbarTarget.hidden = false
    this.bookHostTarget.hidden = false
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true
  }

  showError() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true
    if (this.hasErrorTarget) this.errorTarget.hidden = false
    if (this.hasToolbarTarget) this.toolbarTarget.hidden = true
    this.bookHostTarget.hidden = true
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
